import React, {useState} from 'react';
import {Icon, Image} from 'semantic-ui-react';
import {Formik, Field, FormikProps, FormikHelpers} from 'formik';
import classNames from 'classnames';

import constants from '@constants';
import utils from '@utils';
import mappings from '@mappings';
import {Form, Button, Table, Pagination, Popup} from '@components';
import paths from '@paths';
import {Props} from './types';
import {makeColumns} from './Columns';
import * as handlers from './handlers';
import * as hooks from './hooks';

import './index.scss';

const Main: React.FC<Props> = props => {
  const [loading, setLoading] = useState(true);
  const [isPaging, setIsPaging] = useState(false);
  hooks.useMount(props, {setLoading});

  const initialValues = {
    user: '',
    users: [],
    email: '',
    emails: [],
    organization: '',
    organizations: [],
    position: '',
    positions: [],
    prefecture: null,
    isSMSBounced: false,
    isEmailBounced: false,
  };

  hooks.useMount(props, {setLoading});

  return (
    <div className="a__admin">
      <div className="b__title">ユーザー一覧</div>

      <Formik
        initialValues={initialValues}
        onSubmit={(values: any, formikHelpers: FormikHelpers<any>) =>
          handlers.searchUsers(values, formikHelpers, props, setLoading)
        }
      >
        {(formProps: FormikProps<any>) => {
          return (
            <>
              <div className="w__search-field">
                <div className="w__search-contents">
                  <div className="w__row">
                    <div className="w__field">
                      <Form.Label text="名前" />
                      <div className="w__search-form">
                        <Field
                          component={Form.Input}
                          name="user"
                          placeholder="名前"
                          color="white"
                        />
                        <Button.Icon
                          name="plus"
                          color="yellow"
                          onClick={() => {
                            handlers.addSearchWord(formProps, 'user', 'users');
                          }}
                        />
                      </div>
                      {formProps.values.users.length ? (
                        <div className="w__search-words">
                          <Form.SearchArea
                            searchWords={formProps.values.users}
                            name="users"
                            formProps={formProps}
                          />
                        </div>
                      ) : null}
                    </div>
                    <div className="w__field">
                      <Form.Label text="地域" />
                      <div className="w__search-form">
                        <Field
                          component={Form.Dropdown}
                          name="prefecture"
                          placeholder="地域"
                          color="white"
                          options={utils.mappings.createSelections(
                            mappings.prefectures,
                            true
                          )}
                        />
                      </div>
                    </div>
                    <div className="w__field">
                      <Form.Label text="メールアドレス" />
                      <div className="w__search-form">
                        <Field
                          component={Form.Input}
                          name="email"
                          placeholder="メールアドレス"
                          color="white"
                        />
                        <Button.Icon
                          name="plus"
                          color="yellow"
                          onClick={() => {
                            handlers.addSearchWord(
                              formProps,
                              'email',
                              'emails'
                            );
                          }}
                        />
                      </div>
                      {formProps.values.emails.length ? (
                        <div className="w__search-words">
                          <Form.SearchArea
                            searchWords={formProps.values.emails}
                            name="emails"
                            formProps={formProps}
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="w__row lower">
                    <div className="w__field">
                      <Form.Label text="所属組織" />
                      <div className="w__search-form">
                        <Field
                          component={Form.Input}
                          name="organization"
                          placeholder="所属組織"
                          color="white"
                        />
                        <Button.Icon
                          name="plus"
                          color="yellow"
                          onClick={() => {
                            handlers.addSearchWord(
                              formProps,
                              'organization',
                              'organizations'
                            );
                          }}
                        />
                      </div>
                      {formProps.values.organizations.length ? (
                        <div className="w__search-words">
                          <Form.SearchArea
                            searchWords={formProps.values.organizations}
                            name="organizations"
                            formProps={formProps}
                          />
                        </div>
                      ) : null}
                    </div>

                    <div className="w__field">
                      <Form.Label text="役職" />
                      <div className="w__search-form">
                        <Field
                          component={Form.Input}
                          name="position"
                          placeholder="役職"
                          color="white"
                        />
                        <Button.Icon
                          name="plus"
                          color="yellow"
                          onClick={() => {
                            handlers.addSearchWord(
                              formProps,
                              'position',
                              'positions'
                            );
                          }}
                        />
                      </div>
                      {formProps.values.positions.length ? (
                        <div className="w__search-words">
                          <Form.SearchArea
                            searchWords={formProps.values.positions}
                            name="positions"
                            formProps={formProps}
                          />
                        </div>
                      ) : null}
                    </div>
                    <div className="w__field">
                      <Form.Label text="配信状況" />
                      <div className="w__checkboxes">
                        <Field
                          component={Form.Checkbox}
                          name="isEmailBounced"
                          label="メール配信エラー"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="w__search-button">
                    <Button.Normal
                      large
                      text="検索する"
                      color="yellow"
                      rounded
                      disabled={formProps.isSubmitting || loading}
                      loading={formProps.isSubmitting}
                      onClick={() => formProps.handleSubmit()}
                    />
                  </div>
                </div>
              </div>
            </>
          );
        }}
      </Formik>
      <div className="w__links">
        <div className="b__count">
          合計&nbsp;
          <span className="count">
            {Number(props.users.pagination.totalCount).toLocaleString()}
          </span>
          &nbsp;人
        </div>
        <div className="b__link-column">
          <Button.Normal
            disabled={props.me.plan === constants.PLAN.FREE}
            rounded
            color="green"
            onClick={() => {
              props.history.push(paths.csv.main);
            }}
            className="b__csv-button"
          >
            <Image
              src={constants.IMAGES.CSV}
              className={classNames('b__icon', {
                disabled: props.me.plan === constants.PLAN.FREE,
              })}
            />
            <div className="w__link-section">
              ユーザーをインポート・エクスポートする
            </div>
          </Button.Normal>
          {props.me.plan === constants.PLAN.FREE && (
            <Popup.Normal content="無料プランではCSVによるインポート・エクスポート機能はご利用いただけません。" />
          )}
          <Button.Normal
            className="w__link"
            color="yellow"
            rounded
            onClick={() => {
              props.history.push(paths.users.create);
            }}
          >
            <Icon name="plus" className="b__add-icon" />
            ユーザーを追加する
          </Button.Normal>
        </div>
      </div>
      <div className="b__table">
        <Table.Main
          columns={makeColumns(props, setLoading, setIsPaging, loading)}
          data={props.users.pagination.data.active}
          loading={loading}
          emptyMessage="ユーザーが存在しません"
        />
        <Pagination
          totalCount={props.users.pagination.totalCount}
          current={props.users.pagination.current}
          updateCurrent={async (current: number) => {
            await handlers.updateCurrent(props, current, setIsPaging);
          }}
          disabled={loading || isPaging}
        />
      </div>
    </div>
  );
};

export default Main;
